import { logger } from 'modules/logger';
import { performance } from 'utils/performance';
import { measures } from 'domains/reporter/RichTextEditor/analytics/performance';

let startAudio: HTMLAudioElement | null = null;
let stopAudio: HTMLAudioElement | null = null;
let enabled = false;
let activeAudio: HTMLAudioElement | null = null;
let endedListener: (() => void) | null = null;
let playingListener: (() => void) | null = null;

const initAudio = (): void => {
  if (startAudio != null) return;
  startAudio = new Audio('/audio/dictation-start.mp3');
  stopAudio = new Audio('/audio/dictation-stop.mp3');
  startAudio.load();
  stopAudio.load();
};

const clearEndedListener = (): void => {
  if (activeAudio != null && endedListener != null) {
    activeAudio.removeEventListener('ended', endedListener);
    endedListener = null;
  }
};

const clearPlayingListener = (): void => {
  if (activeAudio != null && playingListener != null) {
    activeAudio.removeEventListener('playing', playingListener);
    playingListener = null;
  }
};

type AudioCueMeasure = (typeof measures.sirona.AudioCue)[keyof typeof measures.sirona.AudioCue];

const playAudio = (audio: HTMLAudioElement, measure: AudioCueMeasure): void => {
  if (activeAudio != null) {
    clearPlayingListener();
    clearEndedListener();
    activeAudio.pause();
    activeAudio.currentTime = 0;
  }
  audio.currentTime = 0;
  activeAudio = audio;
  performance.mark(measure.startMark);
  endedListener = () => {
    clearPlayingListener();
    activeAudio = null;
    endedListener = null;
  };
  audio.addEventListener('ended', endedListener, { once: true });
  playingListener = () => {
    performance.mark(measure.endMark);
    performance.measure(measure.name, measure.startMark, measure.endMark);
    playingListener = null;
  };
  audio.addEventListener('playing', playingListener, { once: true });
  logger.info('audioCueService: playing audio', { src: audio.src });
  audio.play().catch((e) => logger.debug('audioCueService: play rejected', e));
};

export const audioCueService = {
  playStart: (): void => {
    if (!enabled || startAudio == null) return;
    playAudio(startAudio, measures.sirona.AudioCue.PlayStartLatency);
  },
  playStop: (): void => {
    if (!enabled || stopAudio == null) return;
    playAudio(stopAudio, measures.sirona.AudioCue.PlayStopLatency);
  },
  setEnabled: (value: boolean): void => {
    enabled = value;

    if (value) {
      initAudio();
      return;
    }

    if (activeAudio != null) {
      clearPlayingListener();
      clearEndedListener();
      activeAudio.pause();
      activeAudio = null;
    }

    startAudio = null;
    stopAudio = null;
  },
};
